import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, Download, RotateCcw, Save, Hammer } from "lucide-react";

const F = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    {children}
  </label>
);

const INP = "w-full bg-white border border-slate-200 rounded-md px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100";

export default function FixFlipTab({ property }) {
  const [analyses, setAnalyses] = useState([]);
  const [current, setCurrent] = useState(null);
  const [inputs, setInputs] = useState({
    purchase_price: "", arv: "", repair_budget: "",
    holding_months: "4", closing_costs: "", monthly_holding_cost: "",
    financing_costs: "", selling_costs: "", contingency: "", desired_profit: "",
  });
  const [scenarioName, setScenarioName] = useState("Base Case");
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/v3/properties/${property.id}/fixflip`);
      setAnalyses(data.items || []);
      if (data.items?.length > 0) {
        setCurrent(data.items[0]);
        setInputs({ ...inputs, ...data.items[0].inputs });
      } else {
        // Auto-fill from BP-004 published ARV + BP-003 repair estimate + property asking
        setInputs((s) => ({
          ...s,
          purchase_price: property.asking_price || s.purchase_price,
          arv: property.published_arv || property.estimated_arv || s.arv,
          repair_budget: property.estimated_repairs || s.repair_budget,
        }));
      }
    } catch (err) { toast.error("Failed to load Fix & Flip"); }
    finally { setLoading(false); }
  }, [property.id]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setInputs({ ...inputs, [k]: e.target.value });

  const calc = useCallback(async (save = false) => {
    if (!inputs.purchase_price || !inputs.arv || !inputs.repair_budget) {
      toast.error("Purchase, ARV, and Repair Budget are required.");
      return;
    }
    setCalculating(true);
    try {
      const { data } = await api.post(
        `/v3/properties/${property.id}/fixflip/calculate`,
        { inputs, save, scenario_name: scenarioName },
      );
      if (save) {
        setCurrent(data);
        toast.success(`${data.scenario_name} saved`);
        load();
      } else {
        setCurrent({ inputs, outputs: data.outputs, scenario_name: scenarioName });
        toast.success("Calculated");
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Calculation failed"); }
    finally { setCalculating(false); }
  }, [inputs, property.id, scenarioName, load]);

  const runAi = useCallback(async () => {
    if (!current?.id) { toast.error("Save the analysis first."); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post(`/v3/fixflip/${current.id}/ai-review`);
      setCurrent(data);
      toast.success("AI review ready");
    } catch (err) { toast.error("AI review failed"); }
    finally { setAiLoading(false); }
  }, [current]);

  const exportPdf = useCallback(() => {
    if (!current?.id) return;
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/v3/fixflip/${current.id}/export/pdf`, "_blank");
  }, [current]);

  if (loading) return (
    <div className="flex items-center gap-2 text-slate-600 text-sm" data-testid="fixflip-loading">
      <Loader2 size={14} className="animate-spin"/> Loading…
    </div>
  );

  const outs = current?.outputs || {};
  return (
    <div className="space-y-4" data-testid="tab-panel-fixflip">
      {/* Inputs */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-3">Inputs</div>
        <div className="grid gap-3 sm:grid-cols-3">
          <F label="Purchase Price *"><input value={inputs.purchase_price} onChange={set("purchase_price")} data-testid="ff-purchase" className={INP} type="number"/></F>
          <F label="ARV *"><input value={inputs.arv} onChange={set("arv")} data-testid="ff-arv" className={INP} type="number"/></F>
          <F label="Repair Budget *"><input value={inputs.repair_budget} onChange={set("repair_budget")} data-testid="ff-repair" className={INP} type="number"/></F>
          <F label="Holding Months"><input value={inputs.holding_months} onChange={set("holding_months")} className={INP} type="number"/></F>
          <F label="Monthly Holding $ (blank → global default)"><input value={inputs.monthly_holding_cost} onChange={set("monthly_holding_cost")} className={INP} type="number" placeholder="1200"/></F>
          <F label="Closing Costs $ (blank → 2.5% of purchase)"><input value={inputs.closing_costs} onChange={set("closing_costs")} className={INP} type="number" placeholder="auto"/></F>
          <F label="Financing Costs $"><input value={inputs.financing_costs} onChange={set("financing_costs")} className={INP} type="number" placeholder="5% of purchase"/></F>
          <F label="Selling Costs $"><input value={inputs.selling_costs} onChange={set("selling_costs")} className={INP} type="number" placeholder="6% of ARV"/></F>
          <F label="Contingency $"><input value={inputs.contingency} onChange={set("contingency")} className={INP} type="number" placeholder="10% of repairs"/></F>
          <F label="Desired Profit $"><input value={inputs.desired_profit} onChange={set("desired_profit")} className={INP} type="number" placeholder="15% of ARV"/></F>
          <F label="Scenario Name"><input value={scenarioName} onChange={(e) => setScenarioName(e.target.value)} data-testid="ff-name" className={INP}/></F>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={() => calc(false)} disabled={calculating}
                  data-testid="ff-calc"
                  className="inline-flex items-center gap-1 rounded-md bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50">
            {calculating ? <Loader2 size={12} className="animate-spin"/> : <RotateCcw size={12}/>}
            Calculate
          </button>
          <button onClick={() => calc(true)} disabled={calculating}
                  data-testid="ff-save"
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-emerald-500 disabled:opacity-50">
            <Save size={12}/> Save
          </button>
          <button onClick={runAi} disabled={aiLoading || !current?.id}
                  data-testid="ff-ai"
                  className="inline-flex items-center gap-1 rounded-md border border-purple-300 bg-purple-50 text-purple-800 text-xs font-semibold px-3 py-1.5 hover:border-purple-500 disabled:opacity-40">
            {aiLoading ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
            AI Review
          </button>
          <button onClick={exportPdf} disabled={!current?.id}
                  data-testid="ff-pdf"
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-slate-800 text-xs font-semibold px-3 py-1.5 hover:border-slate-400 disabled:opacity-40">
            <Download size={12}/> PDF
          </button>
        </div>
      </div>

      {/* Outputs */}
      {current && (
        <>
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-5" data-testid="ff-results">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-amber-800/80">Net Profit</div>
                <div className="text-4xl font-bold text-slate-900 tabular-nums mt-1" data-testid="ff-net-profit">${fmt(outs.net_profit)}</div>
                <div className="text-xs text-slate-700 mt-1">
                  ROI <b>{pct(outs.roi)}</b> · Margin <b>{pct(outs.profit_margin)}</b> · Total Cost <b>${fmt(outs.total_project_cost)}</b>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-amber-800/80">Recommended Max Purchase</div>
                <div className="text-3xl font-bold text-slate-900 tabular-nums mt-1" data-testid="ff-recommended-max">${fmt(outs.recommended_max_purchase)}</div>
                <div className="text-xs text-slate-700 mt-1">
                  70% Rule <b>${fmt(outs.seventy_percent_purchase)}</b> · Break-even <b>${fmt(outs.break_even_purchase)}</b>
                </div>
              </div>
            </div>
          </div>
          <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
            <Metric label="Gross Profit" value={`$${fmt(outs.gross_profit)}`}/>
            <Metric label="Cash In" value={`$${fmt(outs.cash_in)}`}/>
            <Metric label="Holding Total" value={`$${fmt(outs.holding_total)}`}/>
            <Metric label="Net Sale Proceeds" value={`$${fmt(outs.net_sale_proceeds)}`}/>
          </div>
          {(outs.warnings || []).length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-1" data-testid="ff-warnings">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Warnings</div>
              {outs.warnings.map((w, i) => (
                <div key={i} className="text-[11px] text-slate-800">
                  <span className="text-[9px] px-1 py-0.5 rounded bg-slate-100 text-slate-700 uppercase font-semibold mr-2">{w.severity}</span>
                  {w.message}
                </div>
              ))}
            </div>
          )}
          {current.ai_review && (
            <div className="rounded-xl border border-purple-200 bg-purple-50/50 p-4 space-y-1" data-testid="ff-ai-block">
              <div className="text-[10px] uppercase tracking-wider text-purple-700">AI Review</div>
              <div className="text-sm text-slate-900">{current.ai_review.summary}</div>
              {current.ai_review.purchaseCeilingAdvice && (
                <div className="text-[11px] text-slate-700"><b>Purchase ceiling:</b> {current.ai_review.purchaseCeilingAdvice}</div>
              )}
              {current.ai_review.costDrivers?.length > 0 && (
                <div className="text-[11px] text-slate-700"><b>Cost drivers:</b> {current.ai_review.costDrivers.join("; ")}</div>
              )}
            </div>
          )}
        </>
      )}

      {/* History */}
      {analyses.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ff-history">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Saved Analyses ({analyses.length})</div>
          <div className="space-y-1">
            {analyses.map((a) => (
              <button key={a.id}
                      onClick={() => { setCurrent(a); setInputs({ ...inputs, ...a.inputs }); }}
                      data-testid={`ff-history-${a.id.slice(0,8)}`}
                      className="w-full text-left flex items-center justify-between text-xs px-2 py-1.5 rounded hover:bg-slate-50">
                <span>v{a.version} · {a.scenario_name}</span>
                <span className="tabular-nums text-slate-600">Net ${fmt(a.outputs?.net_profit)} · ROI {pct(a.outputs?.roi)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const Metric = ({ label, value }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3">
    <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-base font-semibold text-slate-900 tabular-nums mt-0.5">{value}</div>
  </div>
);

function fmt(v) {
  if (v == null || v === "") return "—";
  const n = parseFloat(String(v));
  return Number.isNaN(n) ? String(v) : n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function pct(v) {
  if (v == null || v === "") return "—";
  const n = parseFloat(String(v));
  return Number.isNaN(n) ? String(v) : `${(n * 100).toFixed(1)}%`;
}
